<script setup>
// @ts-check

import BaseButton from '@/components/base/BaseButton.vue'
import BaseChecklist from '@/components/base/BaseChecklist.vue'
import FadeTransition from '@/components/transitions/FadeTransition.vue'
import { useFilters } from '@/core/composables/useFilters'
import { onClickOutside, useArrayMap } from '@vueuse/core'
import * as _ from 'lodash-es'
import { ref, toRefs, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

const props = defineProps({
  filters: {
    /** @type {import('vue').PropType<FilterItem[]>} */
    type: Array,
    required: true
  },
  disabled: {
    type: Boolean
  }
})

defineOptions({
  name: 'FilterSelect'
})

const { filters } = toRefs(props)

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const isOpen = ref(false)

const managedFilters = useArrayMap(filters, (filter) => ({
  ...filter,
  isArray: filter.param.endsWith('[]')
}))

const { queryWithoutFilters, getQueryWithFilters, selectedFilters } =
  useFilters(managedFilters, route)

/**
 * State of the filter dropdown.
 * @type {import('vue').Ref<ManagedFilterItem[]>}
 */
const menuFilters = ref(selectedFilters.value)

/**
 * Active filters which correspond to query params and router state.
 * @type {import('vue').Ref<ManagedFilterItem[]>}
 */
const activeFilters = ref(selectedFilters.value)

/** Selectable filter options. */
const filterOptions = useArrayMap(managedFilters, (filter) => ({
  label: filter.label,
  value: filter
}))

/**
 * Resets filter by filters from route query.
 */
const resetFilters = async () => {
  await router.replace({
    query: { ...queryWithoutFilters.value, page: 1 }
  })
}

const openDropdown = () => {
  menuFilters.value = activeFilters.value
  isOpen.value = true
}

const closeDropdown = () => {
  isOpen.value = false
}

/** @param {ManagedFilterItem} filter */
const removeFilter = async (filter) => {
  const currentFilters = getQueryWithFilters(activeFilters.value)

  if (filter.isArray) {
    currentFilters[filter.param] = _.reject(
      currentFilters[filter.param],
      _.partial(_.isEqual, filter.value)
    )
  } else {
    currentFilters[filter.param] = null
  }

  await router.replace({
    query: { ...currentFilters, page: 1 }
  })
}

const selectFilters = async () => {
  // Update locked filters through router.
  await router.replace({
    query: { ...getQueryWithFilters(menuFilters.value), page: 1 }
  })

  closeDropdown()
}

/** @type {import('vue').Ref<?HTMLDivElement>} */
const menu = ref(null)
onClickOutside(menu, closeDropdown)

watchEffect(() => {
  activeFilters.value = selectedFilters.value
})
</script>

<template>
  <div
    class="relative flex min-h-16 flex-wrap justify-between select-none sm:flex-nowrap"
  >
    <div class="flex min-w-28 items-center">
      <button
        v-show="!isOpen"
        class="filter-select-open-button"
        type="button"
        :disabled
        @click="openDropdown"
      >
        <VIcon icon="$filter-outline" size="small" />
        <span class="ml-2 font-normal">
          {{ t('general.filter') }}
        </span>
      </button>
      <button
        v-show="isOpen"
        class="filter-select-close-button"
        type="button"
        :disabled
        :aria-label="t('general.close')"
        @click="closeDropdown"
      >
        <span class="inline-flex items-center">
          <VIcon
            v-if="isOpen"
            icon="$close"
            size="1.25rem"
            class="text-secondary-900"
          />
        </span>
      </button>
    </div>

    <div v-if="activeFilters.length" class="filter-select-active-container">
      <button
        v-for="filter in activeFilters"
        :key="filter.value"
        :disabled
        class="filter-select-active-button"
        type="button"
        @click="removeFilter(filter)"
      >
        <span class="p-1">
          {{ filter.label }}
        </span>
        <VIcon icon="$close" size="1rem" class="p-1 text-black" />
      </button>
    </div>
    <button
      v-if="activeFilters.length"
      class="filter-select-reset-button"
      type="button"
      :disabled
      @click="resetFilters"
    >
      {{ t('general.reset_filters') }}
    </button>

    <FadeTransition>
      <div v-show="isOpen" ref="menu" class="filter-select-menu">
        <BaseChecklist
          v-model="menuFilters"
          class="w-full"
          hover
          :options="filterOptions"
        />
        <BaseButton
          type="button"
          class="mx-3 rounded-full font-normal"
          filled
          :disabled
          @click.stop="selectFilters"
        >
          {{ t('general.select_filters') }}
        </BaseButton>
      </div>
    </FadeTransition>
  </div>
</template>

<style lang="scss" scoped>
@reference '../../styles/main.css';

.filter-select-open-button {
  @apply inline-flex items-center border-0 border-b-2 border-transparent bg-white p-0 pt-1;
  @apply text-sm leading-normal text-secondary-600;
  @apply duration-300 ease-in-out hover:border-secondary-400;
}

.filter-select-close-button {
  @apply -my-2 inline-flex items-center;
  @apply rounded-full border border-transparent bg-secondary-200 p-2;
  @apply leading-normal text-secondary-600;
}

.filter-select-active-button {
  @apply my-1 mr-2 inline-flex items-center whitespace-nowrap;
  @apply rounded-sm border-0 bg-secondary-100 p-1;
  @apply font-secondary text-sm leading-none;
  @apply hover:bg-secondary-200;
}

.filter-select-active-container {
  @apply order-last -my-1 flex w-full items-center overflow-x-auto pt-4;
  @apply sm:order-0 sm:flex-row sm:flex-wrap sm:overflow-hidden sm:pt-0;
}

.filter-select-menu {
  @apply absolute bottom-[-0.2rem] left-0 z-10 flex w-full min-w-60;
  @apply origin-bottom-left translate-y-full;
  @apply flex-col gap-3 items-start;
  @apply rounded-md border border-secondary-100 bg-white pt-2 pb-4 shadow-sm sm:w-auto;
}

.filter-select-reset-button {
  @apply mt-1 self-center whitespace-nowrap;
  @apply border-0 border-b-2 border-transparent bg-white py-0;
  @apply text-sm text-secondary-600;
  @apply duration-300 ease-in-out hover:border-secondary-400;
}
</style>
